import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import {
  PlatformModule,
  PlanTier,
  ModuleDefinition,
  TenantModulesResponse,
  ModuleOperationResult,
  TenantModuleConfig,
  DependencyValidation,
  EnableModuleDto,
  UpdateModuleConfigDto,
  UpdateTenantTierDto,
} from '../types/modules';

// Query keys
export const moduleKeys = {
  all: ['modules'] as const,
  available: () => [...moduleKeys.all, 'available'] as const,
  byCategory: () => [...moduleKeys.all, 'by-category'] as const,
  byTier: (tier: PlanTier) => [...moduleKeys.all, 'by-tier', tier] as const,
  tenant: (tenantId: string) => [...moduleKeys.all, 'tenant', tenantId] as const,
  config: (tenantId: string, moduleId: PlatformModule) => 
    [...moduleKeys.tenant(tenantId), 'config', moduleId] as const,
  enabled: (tenantId: string, moduleId: PlatformModule) => 
    [...moduleKeys.tenant(tenantId), 'enabled', moduleId] as const,
  dependencies: (tenantId: string, moduleId: PlatformModule) => 
    [...moduleKeys.tenant(tenantId), 'dependencies', moduleId] as const,
};

// Get all available modules
export function useAvailableModules() {
  return useQuery({
    queryKey: moduleKeys.available(),
    queryFn: async (): Promise<ModuleDefinition[]> => {
      const response = await api.get('/modules');
      return response.data;
    },
  });
}

// Get modules grouped by category
export function useModulesByCategory() {
  return useQuery({
    queryKey: moduleKeys.byCategory(),
    queryFn: async (): Promise<Record<string, ModuleDefinition[]>> => {
      const response = await api.get('/modules/by-category');
      return response.data;
    },
  });
}

// Get modules for a specific tier
export function useModulesForTier(tier: PlanTier) {
  return useQuery({
    queryKey: moduleKeys.byTier(tier),
    queryFn: async (): Promise<ModuleDefinition[]> => {
      const response = await api.get(`/modules/by-tier/${tier}`);
      return response.data;
    },
  });
}

// Get tenant modules with status - with auto-refresh for real-time updates
export function useTenantModules(tenantId: string | undefined) {
  return useQuery({
    queryKey: moduleKeys.tenant(tenantId || ''),
    queryFn: async (): Promise<TenantModulesResponse> => {
      const response = await api.get(`/modules/tenant/${tenantId}`);
      return response.data;
    },
    enabled: !!tenantId,
    // Refetch every 30 seconds to catch module changes made by Super Admin
    refetchInterval: 30000,
    // Also refetch when window regains focus
    refetchOnWindowFocus: true,
  });
}

// Get module config
export function useModuleConfig(tenantId: string | undefined, moduleId: PlatformModule) {
  return useQuery({
    queryKey: moduleKeys.config(tenantId || '', moduleId),
    queryFn: async (): Promise<TenantModuleConfig | null> => {
      const response = await api.get(`/modules/tenant/${tenantId}/${moduleId}/config`);
      return response.data;
    },
    enabled: !!tenantId,
  });
}

// Check if module is enabled
export function useIsModuleEnabled(tenantId: string | undefined, moduleId: PlatformModule) {
  return useQuery({
    queryKey: moduleKeys.enabled(tenantId || '', moduleId),
    queryFn: async (): Promise<{ moduleId: PlatformModule; isEnabled: boolean }> => {
      const response = await api.get(`/modules/tenant/${tenantId}/${moduleId}/enabled`);
      return response.data;
    },
    enabled: !!tenantId,
  });
}

// Validate dependencies
export function useValidateDependencies(tenantId: string | undefined, moduleId: PlatformModule) {
  return useQuery({
    queryKey: moduleKeys.dependencies(tenantId || '', moduleId),
    queryFn: async (): Promise<DependencyValidation> => {
      const response = await api.get(`/modules/tenant/${tenantId}/${moduleId}/dependencies`);
      return response.data;
    },
    enabled: !!tenantId,
  });
}

// Enable module mutation
export function useEnableModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      moduleId,
      config,
    }: {
      tenantId: string;
      moduleId: PlatformModule;
      config?: Record<string, any>;
    }): Promise<ModuleOperationResult> => {
      const dto: EnableModuleDto = { config };
      const response = await api.post(`/modules/tenant/${tenantId}/${moduleId}/enable`, dto);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: moduleKeys.tenant(variables.tenantId) });
    },
  });
}

// Disable module mutation
export function useDisableModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      moduleId,
    }: {
      tenantId: string;
      moduleId: PlatformModule;
    }): Promise<ModuleOperationResult> => {
      const response = await api.post(`/modules/tenant/${tenantId}/${moduleId}/disable`);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: moduleKeys.tenant(variables.tenantId) });
    },
  });
}

// Update module config mutation
export function useUpdateModuleConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      moduleId,
      config,
    }: {
      tenantId: string;
      moduleId: PlatformModule;
      config: Record<string, any>;
    }): Promise<ModuleOperationResult> => {
      const dto: UpdateModuleConfigDto = { config };
      const response = await api.put(`/modules/tenant/${tenantId}/${moduleId}/config`, dto);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: moduleKeys.tenant(variables.tenantId) });
      queryClient.invalidateQueries({ 
        queryKey: moduleKeys.config(variables.tenantId, variables.moduleId) 
      });
    },
  });
}

// Update tenant tier mutation
export function useUpdateTenantTier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      tier,
    }: {
      tenantId: string;
      tier: PlanTier;
    }): Promise<{ success: boolean; tier: PlanTier; message: string }> => {
      const dto: UpdateTenantTierDto = { tier };
      const response = await api.put(`/modules/tenant/${tenantId}/tier`, dto);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: moduleKeys.tenant(variables.tenantId) });
    },
  });
}

// Helper hook to check if a specific module is enabled for current tenant
export function useModuleEnabled(moduleId: PlatformModule) {
  const { data: tenantModules } = useTenantModules(
    // This would need to get tenantId from auth context
    undefined
  );

  if (!tenantModules) return false;

  const module = tenantModules.modules.find(m => m.id === moduleId);
  return module?.isEnabled || false;
}
